<template>
  <TemplateDetailLayout class="settings-tab">
    <template #option-heading>
      <AppHeading text="Configuración" tag="h3" />
    </template>
    <template #option-content>
      <AppForm>
        <div class="setting-tab__options">
          <!-- CATEGROY -->
          <div class="setting-tab__category">
            <div class="setting-tab__label-wrapper">
              <label class="setting-tab__label">Categoría</label>
            </div>
            <AppMultiselect
              v-model="templateData.category"
              id="category"
              label="name"
              track-by="id"
              placeholder="Selecciona una categoría"
              deselectLabel=""
              :allowEmpty="false"
              :options="categoriesOptions"
            />
          </div>
          <!-- EDITORS -->
          <div class="setting-tab__editors">
            <div class="setting-tab__label-wrapper">
              <label class="setting-tab__label">Editores</label>
              <AppTooltip message="Este cambio se publica automáticamente" />
            </div>
            <AppMultiselect
              id="editors"
              v-model="templateData.editors"
              label="username"
              track-by="id"
              class="user-select"
              placeholder="Buscar..."
              deselectLabel=""
              :hideSelected="true"
              :multiple="true"
              :allowEmpty="false"
              :options="editors"
              @select="editorAdded"
              @remove="editorRemoved"
            />
          </div>
          <!-- TYPE -->
          <div class="setting-tab__type">
            <div class="setting-tab__label-wrapper">
              <label class="setting-tab__label">Tipo</label>
            </div>
            <div class="setting-tab__type-options">
              <AppCheckbox
                id="setting-reccurrent"
                label="Recurrente"
                data-key="recurrent"
                v-model="templateData.recurrent"
              />
              <AppCheckbox
                id="setting-technical"
                label="Técnica"
                data-key="technical"
                v-model="templateData.technical"
              />
            </div>
          </div>
          <!-- SLOTS -->
          <div class="setting-tab__slots">
            <div class="setting-tab__label-wrapper">
              <label class="setting-tab__label">Slots</label>
            </div>
            <AppInput v-model="templateData.slots" type="number" />
            <span v-if="!validSlots" class="setting-tab__input-error">
              Mínimo 1 slot y máximo 3
            </span>
          </div>
          <!-- HS Slug -->
          <div class="setting-tab__hs-slug">
            <div class="setting-tab__label-wrapper">
              <label class="setting-tab__label">Slug de la acción en HubSpot*</label>
            </div>
            <AppInput v-model="templateData.hs_slug" />
            <!-- error -->
            <AppInputError
              v-if="!templateData.hs_slug"
              message="Se necesita el slug de la acción de HubSpot"
            />
          </div>
        </div>
      </AppForm>
    </template>
  </TemplateDetailLayout>
</template>

<script>
// Utilities
import { debounce } from 'debounce'
// Components
import AppInput from 'Ui/AppInput'
import AppHeading from 'Ui/AppHeading'
import AppTooltip from 'Ui/AppTooltip'
import AppCheckbox from 'Ui/AppCheckbox'
import AppForm from 'Components/AppForm'
import AppInputError from 'Ui/AppInputError'
import AppMultiselect from 'Ui/AppMultiselect'
import TemplateDetailLayout from 'Layouts/TemplateDetailLayout'

export default {
  name: 'TemplateEditPageSettings',

  components: {
    AppForm,
    AppInput,
    AppHeading,
    AppTooltip,
    AppCheckbox,
    AppInputError,
    AppMultiselect,
    TemplateDetailLayout,
  },
  props: {
    template: {
      type: Object,
      required: true,
    },
    categories: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      templateData: {
        category: '',
        editors: this.template.editors,
        recurrent: this.template.recurrent,
        technical: this.template.technical,
        slots: this.template.slots,
        hs_slug: this.template.hs_slug,
      },
    }
  },
  methods: {
    editorAdded(editor) {
      this.sendUpdatedEditors({ add_editor: editor.id })
    },

    editorRemoved(editor) {
      this.sendUpdatedEditors({ remove_editor: editor.id })
    },

    sendUpdate(values) {
      if (values.key === 'slots') {
        if (!this.validSlots) return
        return this.$store.dispatch('templates/updateTemplateDraftSlots', { slots: values.slots })
      }

      this.$emit('updated', values)
    },

    sendUpdatedEditors(values) {
      this.$emit('updated-editors', values)
    },

    debounceSlot: debounce(function (values) {
      this.sendUpdate(values)
    }, 2000),

    debounceHsSlug: debounce(function (values) {
      if (!values.hs_slug) return
      this.sendUpdate(values)
    }, 2000),
  },

  computed: {
    editors() {
      return this.$store.getters['user/getEditors']
    },

    editorsUserName() {
      return this.templateData.editors.map((e) => e.username)
    },

    categoriesOptions() {
      const categories = this.categories.map((cat) => {
        return {
          id: cat.id,
          name: cat.name,
        }
      })
      return categories
    },

    validSlots() {
      const slots = this.templateData.slots
      return slots >= 1 && slots <= 3
    },
  },

  watch: {
    // CATEGORY
    'templateData.category': function (newVal, oldVal) {
      if (oldVal === '') return
      this.sendUpdate({ category: newVal.id })
    },

    // RECURRENT
    'templateData.recurrent': function (newVal) {
      this.sendUpdate({ recurrent: newVal })
    },

    // TECHNICAL
    'templateData.technical': function (newVal) {
      this.sendUpdate({ technical: newVal })
    },

    // SLOTS
    'templateData.slots': function (newVal) {
      this.debounceSlot({ key: 'slots', slots: newVal })
    },

    // HS Slug
    'templateData.hs_slug': function (newVal) {
      this.debounceHsSlug({ key: 'hs_slug', hs_slug: newVal })
    },
  },

  created() {
    // setting current category
    this.templateData.category = this.categoriesOptions.filter(
      (cat) => cat.name === this.template.category
    )
  },
}
</script>

<style scoped>
.setting-tab__options {
  display: grid;
  row-gap: 25px;
}

.setting-tab__label-wrapper {
  margin-bottom: 5px;
}

.setting-tab__label {
  font-size: 16px;
  color: var(--c-primary);
}

.setting-tab__label-wrapper .diy-tooltip {
  position: relative;
  top: -8px;
  right: -2px;
}

.setting-tab__type-options {
  display: grid;
  grid-template-columns: auto auto;
  justify-content: flex-start;
  column-gap: 15px;
  margin-top: 10px;
}

.setting-tab__editors >>> .multiselect.multiselect--active.user-select .multiselect__input {
  padding: 10px;
}
</style>
